/* ============================================================================
 * Project        : Deloitte Digital Compliance
 * File           : EESWMSavedLinkageCard.jsx
 * Version        : 1.0.0
 * Author         : Team Neubinary
 * Created On     : 2026-06-24
 *
 * Description    : Digital Compliance for Automation and Governance
 *
 * Change History :
 * ----------------------------------------------------------------------------
 * Date         | Version | Author           | Description
 * ----------------------------------------------------------------------------
 * 2026-06-24   | 1.0.0   | Team Neubinary   | Initial creation
 * <YYYY-MM-DD> | x.x.x   | <Name/Team>      | <Change description>
 * ============================================================================
 *
 * Copyright (c) 2026 AU79 Consulting Services Pvt. Ltd.
 * All Rights Reserved.
 *
 * This source code and its contents are confidential and proprietary to
 * AU79 Consulting Services Pvt. Ltd. Unauthorized copying, reproduction,
 * modification, distribution, or disclosure of this material, in whole or
 * in part, is strictly prohibited without prior written permission from
 * AU79 Consulting Services Pvt. Ltd.
 * ============================================================================
 */
import { Box, Typography, IconButton } from "@mui/material";
import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { componentStyle } from "../../../config/appTheme";
import CloseIcon from "@mui/icons-material/Close";

const DEFAULT_EE_CONFIG = {
  idKey: "ee_ids",
  displayKey: "Assembly Number ",
  displayValue: (item) => {
    if (Array.isArray(item?.addOn)) {
      return item.addOn.map((ee) => ee?.assembly_number).join(", ");
    }
    return item?.addOn?.assembly_number;
  },
};

const getKeyBasedOnEntity = (entityType) => {
  switch (entityType) {
    case "swm":
      return {
        idKey: "swm_ids",
        displayKey: "Number ",
        displayValue: (item) => {
          if (Array.isArray(item?.addOn)) {
            return item.addOn.map((swm) => swm?.name).join(", ");
          }
          return item?.addOn?.name;
        },
      };
    case "vs":
      return {
        idKey: "vs_id",
        displayKey: "Name",
        displayValue: (item) => item?.addOn?.name,
      };
    case "ee":
    default:
      return DEFAULT_EE_CONFIG;
  }
};

const checkIsMatch = (linkage, item, idKey) => {
  if (linkage.ee_ids && item.ee_ids) {
    return JSON.stringify(linkage.ee_ids) === JSON.stringify(item.ee_ids);
  }
  if (linkage.swm_ids && item.swm_ids) {
    return JSON.stringify(linkage.swm_ids) === JSON.stringify(item.swm_ids);
  }
  return linkage[idKey] === item[idKey];
};

function EESWMSavedLinkageCard({
  savedLinkages,
  setSavedLinkages,
  entityType,
}) {
  const [modifiedLinkItems, setModifiedLinkItems] = useState([]);
  const entityConfig = getKeyBasedOnEntity(entityType);

  useEffect(() => {
    const array =
      savedLinkages?.flatMap(
        (item) =>
          item.legal?.map((legalItem) => ({
            ...legalItem,
            ...item,
          })) || [],
      ) || [];
    setModifiedLinkItems(array);
  }, [savedLinkages]);

  const handleRemoveLinkage = (item) => {
    const updatedLinkages = savedLinkages
      .map((linkage) => {
        const isMatch = checkIsMatch(linkage, item, entityConfig.idKey);

        if (isMatch) {
          const filteredLegal = linkage.legal.filter((legal) => {
            if (legal.lr_ids && item.lr_ids) {
              return (
                JSON.stringify(legal.lr_ids) !== JSON.stringify(item.lr_ids)
              );
            }
            return legal.lr_id !== item.lr_id;
          });

          return filteredLegal.length > 0
            ? { ...linkage, legal: filteredLegal }
            : null;
        }

        return linkage;
      })
      .filter(Boolean);

    setSavedLinkages(updatedLinkages);
  };

  return (
    <Box>
      {modifiedLinkItems?.map((item, index) => {
        const itemKey =
          item?.lr_id ||
          item?.id ||
          (item?.description ? `${item.description}-${index}` : index);

        return (
          <Box
            key={itemKey}
            sx={{
              mb: 2,
              ...componentStyle.font.text,
              color: "white",
              backgroundColor: "black",
              display: "flex",
              gap: 2,
              p: 1,
            }}
          >
            <IconButton
              onClick={() => handleRemoveLinkage(item)}
              sx={{ color: "white", ml: 1, mr: 1, mt: -1 }}
            >
              <CloseIcon />
            </IconButton>
            <Typography>{`${entityConfig.displayKey}: ${entityConfig.displayValue(item)}`}</Typography>
            <Typography>{`Description: ${item?.description}`}</Typography>
          </Box>
        );
      })}
    </Box>
  );
}

EESWMSavedLinkageCard.propTypes = {
  savedLinkages: PropTypes.oneOfType([
    PropTypes.arrayOf(
      PropTypes.shape({
        ee_ids: PropTypes.any,
        swm_ids: PropTypes.any,
        legal: PropTypes.array,
        addOn: PropTypes.any,
      }),
    ),
    PropTypes.shape({
      flatMap: PropTypes.func,
      map: PropTypes.func,
    }),
  ]),
  setSavedLinkages: PropTypes.func,
  entityType: PropTypes.string,
};

export default EESWMSavedLinkageCard;
